// apps/cli/src/lib/bootstrap-aci.ts
import { runAz } from "./az.js";

export interface GitHubAppCreds {
  appId: string;
  slug: string;
  org: string;
  installationId: string;
  /** base64-encoded PEM — treated as a secret, passed as a secure env var. */
  pemB64: string;
}

export interface AciSpec {
  aciName: string;
  resourceGroup: string;
  location: string;
  subscriptionId: string;
  miResourceId: string;
  miClientId: string;
  appRegClientId: string;
  image: string;
  gatewayImageRef?: string;
  foundryTracing?: string;
  /**
   * Release-channel manifest URL. Threaded to the install so the deployed gateway
   * and the updater job poll the same channel. Absent ⇒ both fall back to their
   * baked default channel.
   */
  updateChannelUrl?: string;
  githubApp?: GitHubAppCreds;
  /**
   * The platform release this install IS (bare X.Y.Z).
   *
   * The engine derives the release manifest's URL from this, fetches it, and
   * deploys what it names. Travels with `releaseManifestSha256` — see there.
   *
   * Absent ⇒ the engine has not been told which release it is installing and
   * REFUSES, unless the launch explicitly opted into an unreleased-debug install.
   * It used to fall back to a version baked into the image; that fallback is gone.
   * The baked value was a photocopy of a pin taken at image-build time, and under
   * publish→select it is structurally at least one declaration stale — reading it
   * as an original is the defect this rail exists to end.
   */
  platformVersion?: string;
  /**
   * `sha256:<hex>` over the release manifest's bytes AS SERVED, taken by this CLI
   * at launch (see lib/manifest-seal.ts).
   *
   * The engine fetches the same URL independently and refuses unless its bytes
   * hash to this. Two independent fetches that must agree — which is what turns
   * "the engine deploys the manifest" from a hope into a check. It is not a
   * signature: an asset replaced before this CLI ever looked is a releases-repo
   * threat that only a signed manifest closes, and that is a named follow-on.
   *
   * Passed with `platformVersion` or not at all: the version says what to fetch,
   * the digest says what the bytes must be, and one without the other seals
   * nothing.
   */
  releaseManifestSha256?: string;
  /**
   * This launch deliberately points at an engine no release manifest names, and
   * consents to an install that claims no release version.
   *
   * The engine REFUSES when it is told no version, because every real launcher
   * passes one and silence is a bug signal. This is the one thing that says the
   * silence was meant: a maintainer pointing `--installer-tag` at a development
   * build. It is set by that decision (see lib/bootstrap-launch-target.ts), never
   * by a founder, and the install stamps `unreleased-debug` rather than pretending
   * to be a release.
   */
  unreleasedDebug?: boolean;
  /** Install without brain repos or the brain-backed executors. Test/CI rigs only. */
  skipBrains?: boolean;
  /**
   * The founder's AAD object id — who to grant the platform's data-plane roles to.
   *
   * The installer holds Owner at subscription scope and already self-grants the
   * exact two roles a human needs (`Foundry User`, and a Key Vault secrets role)
   * at step 3b. The ONLY thing it has never known is who the founder is, which is
   * why granting them was pushed out to a command the founder ran afterwards.
   * This is that missing ingredient, and it is non-secret (an object id, not a
   * credential), so it belongs in the plain env block.
   *
   * Absent ⇒ the installer skips the founder grant entirely and the founder falls
   * back to `m8t prereqs --fix`. Never a reason to fail an install.
   */
  founderObjectId?: string;
  /**
   * Opt-in registration details, threaded only when the operator passed the
   * matching flag. Non-secret registration data, not credentials, so they
   * belong in the plain env block (not --secure-environment-variables).
   * Absent ⇒ the installer enrolls pseudonymously.
   */
  enrollContactEmail?: string;
  enrollCompany?: string;
  /**
   * The install id, minted by the launcher.
   *
   * The container mints one itself when nothing supplies it (installer/lib.sh),
   * which is what the CLI path did until now — with the consequence that the
   * id existed only inside the container and nothing local could ever join a
   * CLI-driven install to its own telemetry. Supplying it here puts the CLI on
   * the same footing as the web path, which has always minted the id in the page
   * and threaded it page -> ARM parameter -> container env.
   */
  installId?: string;
}

/**
 * Build the `az container create` args (MI attach + the runtime env).
 *
 * Uses `--restart-policy OnFailure` so a transient first-command death (e.g. MI
 * propagation lag before `login_with_retry` was introduced) triggers a container
 * restart and self-heals. The entrypoint is idempotent (hydrate_status restores
 * prior progress on restart). `fail()` exits 0 after writing the `failed` status
 * blob — the blob is the source of truth, so an honest failure stops ACI from
 * restart-cycling indefinitely.
 */
export function buildAciCreateArgs(s: AciSpec): string[] {
  // Plain (non-secret) environment variables — one --environment-variables block.
  const env = [
    `SUBSCRIPTION_ID=${s.subscriptionId}`,
    `RESOURCE_GROUP=${s.resourceGroup}`,
    `LOCATION=${s.location}`,
    `MI_CLIENT_ID=${s.miClientId}`,
    `APP_REG_CLIENT_ID=${s.appRegClientId}`,
    // The installer's own image ref, so the install can provision the updater job
    // to run THIS engine. Derived from the launched image rather than threaded
    // separately, so the two can never disagree — and never read from the release
    // pin, which can name an installer several versions behind the running one.
    `INSTALLER_IMAGE_REF=${s.image}`,
  ];
  if (s.platformVersion) env.push(`M8T_PLATFORM_VERSION=${s.platformVersion}`);
  if (s.releaseManifestSha256) env.push(`M8T_RELEASE_MANIFEST_SHA256=${s.releaseManifestSha256}`);
  if (s.unreleasedDebug) env.push("M8T_UNRELEASED_DEBUG=1");
  if (s.gatewayImageRef) env.push(`GATEWAY_IMAGE_REF=${s.gatewayImageRef}`);
  if (s.foundryTracing) env.push(`FOUNDRY_TRACING=${s.foundryTracing}`);
  if (s.skipBrains) env.push("SKIP_BRAINS=true");
  if (s.founderObjectId) env.push(`FOUNDER_OBJECT_ID=${s.founderObjectId}`);
  if (s.updateChannelUrl) env.push(`M8T_UPDATE_CHANNEL_URL=${s.updateChannelUrl}`);
  if (s.enrollContactEmail) env.push(`M8T_ENROLL_CONTACT_EMAIL=${s.enrollContactEmail}`);
  if (s.enrollCompany) env.push(`M8T_ENROLL_COMPANY=${s.enrollCompany}`);
  if (s.installId) env.push(`M8T_INSTALL_ID=${s.installId}`);
  if (s.githubApp) {
    // Non-secret GitHub App metadata is safe in the plain env block.
    const g = s.githubApp;
    env.push(
      `GITHUB_ORG=${g.org}`,
      `GH_APP_ID=${g.appId}`,
      `GH_APP_SLUG=${g.slug}`,
      `GH_INSTALLATION_ID=${g.installationId}`,
    );
  }

  const args = [
    "container", "create",
    "--name", s.aciName,
    "--resource-group", s.resourceGroup,
    "--location", s.location,
    "--subscription", s.subscriptionId,
    "--image", s.image,
    "--os-type", "Linux",
    "--restart-policy", "OnFailure",
    "--assign-identity", s.miResourceId,
    "--cpu", "1", "--memory", "1.5",
    "--environment-variables", ...env,
  ];

  if (s.githubApp) {
    // The PEM is a secret — pass it via --secure-environment-variables so az
    // CLI redacts it from logs and the ACI properties API response.
    args.push("--secure-environment-variables", `GH_APP_PEM_B64=${s.githubApp.pemB64}`);
  }

  args.push("--only-show-errors");
  return args;
}

export async function kickInstaller(s: AciSpec): Promise<void> {
  await runAz(buildAciCreateArgs(s));
}
